import React, { useState, useEffect } from 'react';
import { inputSourcesAPI } from '../services/api';
import { Database, Play, X, ChevronDown } from 'lucide-react';

// Examples will be generated dynamically based on available collections
const getDefaultExamples = (collections) => {
  const examples = [];
  const collectionNames = collections.map(c => c.name);
  
  // Example for hosts collection
  if (collectionNames.includes('hosts')) {
    examples.push({
      name: "All hosts",
      mode: "collection",
      collection: "hosts",
      filter: {},
      projection: { host: 1, ip: 1, status: 1 },
      jq_filter: ""
    });
    examples.push({
      name: "Host names (extract with jq)",
      mode: "collection",
      collection: "hosts",
      filter: {},
      projection: { host: 1 },
      jq_filter: ".[].host"
    });
    examples.push({
      name: "URLs with 200 status code",
      mode: "aggregate",
      collection: "hosts",
      pipeline: [
        { $unwind: "$locations" },
        { $match: { "locations.status_code": 200 } },
        { $project: { url: "$locations.url", _id: 0 } }
      ],
      jq_filter: ""
    });
    examples.push({
      name: ".js & .txt URLs",
      mode: "aggregate",
      collection: "hosts",
      pipeline: [
        { $unwind: "$locations" },
        { $match: {
          $or: [
            { "locations.url": { $regex: "\\.js$", $options: "i" } },
            { "locations.url": { $regex: "\\.txt$", $options: "i" } }
          ]
        }},
        { $project: { url: "$locations.url", _id: 0 } }
      ],
      jq_filter: ""
    });
    examples.push({
      name: '"admin" URLs',
      mode: "aggregate",
      collection: "hosts",
      pipeline: [
        { $unwind: "$locations" },
        { $match: { "locations.url": { $regex: "admin", $options: "i" } } },
        { $project: { url: "$locations.url", _id: 0 } }
      ],
      jq_filter: ""
    });
    examples.push({
      name: '"admin" and "internal" subdomains',
      mode: "collection",
      collection: "hosts",
      filter: {},
      projection: { host: 1 },
      jq_filter: '.[] | select(.host | test("(admin|internal)"; "i")) | .host'
    });
  }

  // Example for vulnerabilities collection
  if (collectionNames.includes('vulnerabilities')) {
    examples.push({
      name: "Links on vulnerable hosts",
      mode: "collection",
      collection: "vulnerabilities",
      filter: {},
      projection: { host: 1, url: 1, title: 1, severity: 1 },
      jq_filter: '.[] | {vulnerability: .title, severity: .severity, host: .host, url: .url}'
    });
  }
  
  // Example for tasks collection
  if (collectionNames.includes('tasks')) {
    examples.push({
      name: "Completed tasks",
      mode: "collection",
      collection: "tasks",
      filter: { status: "completed" },
      projection: { tool: 1, result_id: 1 },
      jq_filter: ""
    });
  }
  
  // Example for config collection
  if (collectionNames.includes('config')) {
    examples.push({
      name: "Config data",
      mode: "collection",
      collection: "config",
      filter: {},
      projection: {},
      jq_filter: ""
    });
  }
  
  // Example for hosts with vulnerabilities
  if (collectionNames.includes('vulnerabilities')) {
    examples.push({
      name: "Хосты по которым есть файндинги из vulnerabilities",
      mode: "aggregate",
      collection: "vulnerabilities",
      pipeline: [
        {
          $group: {
            _id: "$host",
            count: { $sum: 1 }
          }
        },
        {
          $project: {
            host: "$_id",
            _id: 0
          }
        }
      ],
      jq_filter: ".[].host"
    });
  }
  
  // Generic example if we have any collection
  if (collectionNames.length > 0) {
    const firstCollection = collectionNames[0];
    examples.push({
      name: `All documents from ${firstCollection}`,
      mode: "collection",
      collection: firstCollection,
      filter: {},
      projection: {},
      jq_filter: ""
    });
  }
  
  return examples;
};

const MongoQueryBuilder = ({ isOpen, onClose, onConfirm }) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [queryMode, setQueryMode] = useState('collection'); // 'collection' or 'aggregate'
  const [filter, setFilter] = useState('{}');
  const [projection, setProjection] = useState('{}');
  const [pipeline, setPipeline] = useState('[]');
  const [jqFilter, setJqFilter] = useState('');
  const [previewResults, setPreviewResults] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [showExamples, setShowExamples] = useState(false);
  const [examples, setExamples] = useState([]);

  // Load collections and reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCollections();
      // Reset state when modal opens
      setPreviewLoading(false);
      setPreviewError('');
      setPreviewResults(null);
      setShowExamples(false);
    }
  }, [isOpen]);

  const loadCollections = async () => {
    try {
      const response = await inputSourcesAPI.getMongoCollections();
      const loadedCollections = response.data.collections || [];
      setCollections(loadedCollections);
      // Generate examples based on available collections
      setExamples(getDefaultExamples(loadedCollections));
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  };

  const handleExampleSelect = (example) => {
    setQueryMode(example.mode);
    setSelectedCollection(example.collection);
    
    // Handle filter - use empty object if not provided
    const filterObj = example.filter || {};
    setFilter(Object.keys(filterObj).length > 0 ? JSON.stringify(filterObj, null, 2) : '{}');
    
    // Handle projection - use empty object if not provided
    const projObj = example.projection || {};
    setProjection(Object.keys(projObj).length > 0 ? JSON.stringify(projObj, null, 2) : '{}');
    
    // Handle pipeline - use empty array if not provided
    const pipelineArr = example.pipeline || [];
    setPipeline(pipelineArr.length > 0 ? JSON.stringify(pipelineArr, null, 2) : '[]');
    
    setJqFilter(example.jq_filter || '');
    setShowExamples(false);
  };

  const handlePreview = async () => {
    if (!selectedCollection) {
      setPreviewError('Please select a collection');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewResults(null);

    try {
      const payload = {
        mode: queryMode,
        collection: selectedCollection,
        limit: 50
      };

      if (queryMode === 'collection') {
        try {
          payload.filter = JSON.parse(filter || '{}');
        } catch (e) {
          throw new Error('Invalid JSON in filter: ' + e.message);
        }
        try {
          const proj = JSON.parse(projection || '{}');
          if (Object.keys(proj).length > 0) {
            payload.projection = proj;
          }
        } catch (e) {
          throw new Error('Invalid JSON in projection: ' + e.message);
        }
      } else {
        try {
          payload.pipeline = JSON.parse(pipeline || '[]');
        } catch (e) {
          throw new Error('Invalid JSON in pipeline: ' + e.message);
        }
      }

      if (jqFilter.trim()) {
        payload.jq_filter = jqFilter.trim();
      }

      const response = await inputSourcesAPI.executeMongoQuery(payload);
      setPreviewResults(response.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || err.message || 'Failed to execute query');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewResults || !previewResults.results || previewResults.results.length === 0) {
      setPreviewError('Please run preview first to get results');
      return;
    }

    // Execute full query (without limit) to get all results
    setPreviewLoading(true);
    try {
      const payload = {
        mode: queryMode,
        collection: selectedCollection,
        limit: 10000 // Get more results for actual execution
      };

      if (queryMode === 'collection') {
        payload.filter = JSON.parse(filter || '{}');
        const proj = JSON.parse(projection || '{}');
        if (Object.keys(proj).length > 0) {
          payload.projection = proj;
        }
      } else {
        payload.pipeline = JSON.parse(pipeline || '[]');
      }

      if (jqFilter.trim()) {
        payload.jq_filter = jqFilter.trim();
      }

      const response = await inputSourcesAPI.executeMongoQuery(payload);
      const results = response.data.results || [];
      
      // Build query params object for template saving (without limit)
      const queryParams = {
        mode: queryMode,
        collection: selectedCollection,
      };
      if (queryMode === 'collection') {
        queryParams.filter = JSON.parse(filter || '{}');
        const proj = JSON.parse(projection || '{}');
        if (Object.keys(proj).length > 0) {
          queryParams.projection = proj;
        }
      } else {
        queryParams.pipeline = JSON.parse(pipeline || '[]');
      }
      if (jqFilter.trim()) {
        queryParams.jq_filter = jqFilter.trim();
      }

      // Call onConfirm with the results as a string (one per line) and query params
      onConfirm(results.join('\n'), queryParams);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Failed to execute full query');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '900px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: 'var(--text-primary)',
          }}>
            <Database size={28} />
            MongoDB Query Builder
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#6b7280',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Examples dropdown */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowExamples(!showExamples)}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ChevronDown size={16} />
            Load Example Query
          </button>
          {showExamples && (
            <div style={{
              marginTop: '8px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: 'var(--bg-tertiary)',
              maxHeight: '200px',
              overflow: 'auto',
            }}>
              {examples.length > 0 ? (
                examples.map((example, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleExampleSelect(example)}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: idx < examples.length - 1 ? '1px solid var(--border-color)' : 'none',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      {example.name}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {example.mode === 'collection' ? 'Simple Query' : 'Aggregation Pipeline'} • Collection: {example.collection}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                  No examples available. Please select a collection first.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Collection selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: 'var(--text-primary)',
          }}>
            Collection
          </label>
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="">Select a collection...</option>
            {collections.map((coll) => (
              <option key={coll.name} value={coll.name}>
                {coll.name || coll.full_name} ({coll.estimated_count !== null && coll.estimated_count !== undefined ? coll.estimated_count.toLocaleString() : '?'} docs)
              </option>
            ))}
          </select>
        </div>

        {/* Query mode toggle */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: 'var(--text-primary)',
          }}>
            Query Mode
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setQueryMode('collection')}
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${queryMode === 'collection' ? '#3b82f6' : 'var(--border-color)'}`,
                borderRadius: '6px',
                backgroundColor: queryMode === 'collection' ? '#3b82f615' : 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: queryMode === 'collection' ? '600' : 'normal',
              }}
            >
              Simple Query
            </button>
            <button
              onClick={() => setQueryMode('aggregate')}
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${queryMode === 'aggregate' ? '#3b82f6' : 'var(--border-color)'}`,
                borderRadius: '6px',
                backgroundColor: queryMode === 'aggregate' ? '#3b82f615' : 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: queryMode === 'aggregate' ? '600' : 'normal',
              }}
            >
              Aggregation Pipeline
            </button>
          </div>
        </div>

        {/* Query inputs */}
        {queryMode === 'collection' ? (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}>
                Filter (JSON)
              </label>
              <textarea
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder='{"status": "alive"}'
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '10px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}>
                Projection (JSON, optional)
              </label>
              <textarea
                value={projection}
                onChange={(e) => setProjection(e.target.value)}
                placeholder='{"host": 1, "ip": 1}'
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
            </div>
          </>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: 'var(--text-primary)',
            }}>
              Aggregation Pipeline (JSON Array)
            </label>
            <textarea
              value={pipeline}
              onChange={(e) => setPipeline(e.target.value)}
              placeholder='[{"$match": {"tool": "httpx"}}, {"$unwind": "$data"}]'
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '10px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        {/* JQ Filter */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: 'var(--text-primary)',
          }}>
            JQ Filter (optional)
          </label>
          <input
            type="text"
            value={jqFilter}
            onChange={(e) => setJqFilter(e.target.value)}
            placeholder='.[].host'
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
            }}
          />
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Extract specific fields from results (e.g., .[].host, .[].data[].url)
          </div>
        </div>

        {/* Preview button */}
        <button
          onClick={handlePreview}
          disabled={previewLoading || !selectedCollection}
          style={{
            width: '100%',
            padding: '12px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: '#3b82f6',
            color: 'white',
            cursor: previewLoading || !selectedCollection ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '20px',
            opacity: previewLoading || !selectedCollection ? 0.5 : 1,
          }}
        >
          <Play size={18} />
          {previewLoading ? 'Running Preview...' : 'Run Preview (50 results)'}
        </button>

        {/* Error */}
        {previewError && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee',
            border: '1px solid #f88',
            borderRadius: '6px',
            color: '#c00',
            fontSize: '14px',
            marginBottom: '16px',
          }}>
            {previewError}
          </div>
        )}

        {/* Preview results */}
        {previewResults && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '12px',
              color: 'var(--text-primary)',
            }}>
              Preview Results ({previewResults.count} of {previewResults.limit} max)
            </h3>
            <div style={{
              maxHeight: '200px',
              overflow: 'auto',
              padding: '12px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: 'var(--text-primary)',
            }}>
              {previewResults.results && previewResults.results.length > 0 ? (
                previewResults.results.map((item, idx) => (
                  <div key={idx} style={{ padding: '2px 0' }}>
                    {typeof item === 'string' ? item : JSON.stringify(item)}
                  </div>
                ))
              ) : (
                <div style={{ color: 'var(--text-secondary)' }}>No results found</div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '500',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!previewResults || previewLoading}
            style={{
              padding: '12px 32px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#10b981',
              color: 'white',
              cursor: !previewResults || previewLoading ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              opacity: !previewResults || previewLoading ? 0.5 : 1,
            }}
          >
            Use These Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default MongoQueryBuilder;

